import Vue from "vue";

import { SetupBiometricsStatus } from "@/plugins/native-app";
import { Gateway } from "@/utils/gateway";
import Modals from "@/utils/modals";

export interface BiometricsHelper {
  setup(vue: Vue, gateway: Gateway): Promise<void>;
}

/**
 * Registers the current device for biometrics on the server and then saves the secret generated by the server to the
 * native app using the user's biometrics so that the user can login with biometrics in the future.
 *
 * This is common functionality that is used by the Sign In and 2FA pages. It is defined here so that it doesn't have
 * to be duplicated in both
 *
 * @param vue the Component
 * @param gateway the Gateway instance
 * @returns an empty promise that is rejected if there is an API error
 */
export async function setup(vue: Vue, gateway: Gateway): Promise<void> {
  const secret = await gateway.setupBiometrics();
  if (!secret) {
    await Modals.biometric.setup.showUnknownError(vue);
    return;
  }

  const result = await vue.$nativeApp.setupBiometrics(secret);
  if (result.status !== SetupBiometricsStatus.SUCCESS) {
    switch (result.status) {
      // If the user canceled, no need to show them a modal saying they canceled, just log them in
      case SetupBiometricsStatus.CANCELED:
        break;

      case SetupBiometricsStatus.NOT_CONFIGURED:
        await Modals.biometric.setup.showNotConfigured(vue);
        break;

      case SetupBiometricsStatus.NO_PERMISSION:
        await Modals.biometric.setup.showNoPermission(vue);
        break;

      case SetupBiometricsStatus.FAILED:
        await Modals.biometric.setup.showFailed(vue);
        break;

      case SetupBiometricsStatus.LOCKED_OUT:
        await Modals.biometric.setup.showLockedOut(vue);
        break;

      default:
        await Modals.biometric.setup.showUnknownError(vue);
    }
  }
}
